import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search,
    Radar,
    AlertCircle,
    Loader2,
    Filter,
    RefreshCw,
    MapPin,
    Sparkles,
    CheckCircle2,
    Star,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { plcspTour } from "@/tours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    searchPlcsp,
    searchPlcspAi,
    importFromPlcsp,
    listPlcspCommunities,
    getPlcspBreakdown,
    createSavedSearch,
} from "@/lib/api";
import { toast } from "sonner";
import PlcspResultCard from "@/components/plcsp/PlcspResultCard";
import PlcspAiBanner from "@/components/plcsp/PlcspAiBanner";
import PlcspEmptyState from "@/components/plcsp/PlcspEmptyState";
import PlcspWatchlistButton from "@/components/plcsp/PlcspWatchlistButton";

export default function Plcsp() {
    const tour = useTour("tour:plcsp", plcspTour, { autoStart: true });
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [cpv, setCpv] = useState("");
    const [minBudget, setMinBudget] = useState("");
    const [maxBudget, setMaxBudget] = useState("");
    const [state, setState] = useState("");
    const [procedureType, setProcedureType] = useState(""); // "", "ordinaria", "menor"
    const [contractType, setContractType] = useState(""); // "", "obras", "suministros", "servicios", "otros"
    const [ambito, setAmbito] = useState(""); // "", "estatal", "agregada"
    const [municipio, setMunicipio] = useState("");
    const [provincia, setProvincia] = useState("");
    const [comunidad, setComunidad] = useState(""); // community key (e.g., "madrid")
    const [communities, setCommunities] = useState([]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("recent"); // recent (interleaved) | deadline | budget_desc | budget_asc
    const [breakdown, setBreakdown] = useState(null); // {contract_type: {...}, ambito: {...}, total}
    const perPage = 20;

    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalIncludingClosed, setTotalIncludingClosed] = useState(0);
    const [aiKeywords, setAiKeywords] = useState([]);
    const [aiCpvCodes, setAiCpvCodes] = useState([]);
    const [aiInterpretation, setAiInterpretation] = useState("");
    const [aiMode, setAiMode] = useState(false); // last search was AI
    const [fetchedAt, setFetchedAt] = useState(null);
    const [cacheSize, setCacheSize] = useState(0);
    const [cacheWarming, setCacheWarming] = useState(false);
    const [openOnly, setOpenOnly] = useState(true);
    const [loading, setLoading] = useState(true);
    const [importingId, setImportingId] = useState(null);
    const [importingWithPdfs, setImportingWithPdfs] = useState(null);
    const [imported, setImported] = useState(new Set());
    const [postImport, setPostImport] = useState(null); // {tender, hasPdfs, pendingExternal}
    const [error, setError] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [useAiSearch, setUseAiSearch] = useState(true); // AI on by default

    const runSearch = useCallback(
        async (pageNum = 1, useAi = false) => {
            setLoading(true);
            if (useAi) setAiLoading(true);
            setError(null);
            try {
                if (useAi) {
                    if (!q.trim()) {
                        setError("Escribe una palabra clave para la búsqueda IA");
                        setLoading(false);
                        setAiLoading(false);
                        return;
                    }
                    const body = {
                        q: q.trim(),
                        page: pageNum,
                        per_page: perPage,
                        open_only: openOnly,
                    };
                    if (cpv.trim()) body.cpv = cpv.trim();
                    if (state) body.state = state;
                    if (procedureType) body.procedure_type = procedureType;
                    if (ambito) body.ambito = ambito;
                    if (minBudget) body.min_budget = Number(minBudget);
                    if (maxBudget) body.max_budget = Number(maxBudget);
                    if (municipio.trim()) body.municipio = municipio.trim();
                    if (provincia.trim()) body.provincia = provincia.trim();
                    if (comunidad) body.comunidad = comunidad;
                    const data = await searchPlcspAi(body);
                    setResults(data.results);
                    setTotal(data.total);
                    setTotalIncludingClosed(
                        typeof data.total_including_closed === "number"
                            ? data.total_including_closed
                            : data.total
                    );
                    setAiKeywords(data.ai_keywords || []);
                    setAiCpvCodes(data.ai_cpv_codes || []);
                    setAiInterpretation(data.ai_interpretation || "");
                    setAiMode(true);
                    setCacheSize(data.cache_size || 0);
                    setCacheWarming(Boolean(data.cache_warming));
                    setPage(data.page);
                } else {
                    const params = {
                        page: pageNum,
                        per_page: perPage,
                        open_only: openOnly,
                    };
                    if (q.trim()) params.q = q.trim();
                    if (cpv.trim()) params.cpv = cpv.trim();
                    if (state) params.state = state;
                    if (procedureType) params.procedure_type = procedureType;
                    if (contractType) params.contract_type = contractType;
                    if (ambito) params.ambito = ambito;
                    if (minBudget) params.min_budget = Number(minBudget);
                    if (maxBudget) params.max_budget = Number(maxBudget);
                    if (municipio.trim()) params.municipio = municipio.trim();
                    if (provincia.trim()) params.provincia = provincia.trim();
                    if (comunidad) params.comunidad = comunidad;
                    if (sortBy && sortBy !== "recent") params.sort = sortBy;
                    const data = await searchPlcsp(params);
                    setResults(data.results);
                    setTotal(data.total);
                    setTotalIncludingClosed(
                        typeof data.total_including_closed === "number"
                            ? data.total_including_closed
                            : data.total
                    );
                    setAiMode(false);
                    setAiKeywords([]);
                    setAiCpvCodes([]);
                    setAiInterpretation("");
                    setFetchedAt(data.fetched_at);
                    setCacheSize(data.cache_size || 0);
                    setCacheWarming(Boolean(data.cache_warming));
                    setPage(data.page);
                }
            } catch (e) {
                setError(
                    e?.response?.data?.detail ||
                        "No se pudo conectar con la PLCSP. Inténtalo de nuevo."
                );
                setResults([]);
                setTotal(0);
            } finally {
                setLoading(false);
                setAiLoading(false);
            }
        },
        [q, cpv, state, procedureType, contractType, ambito, minBudget, maxBudget, municipio, provincia, comunidad, openOnly, sortBy]
    );

    // Load the autonomous communities for the dropdown (one-shot)
    useEffect(() => {
        listPlcspCommunities()
            .then((d) => setCommunities(d?.communities || []))
            .catch(() => setCommunities([]));
    }, []);

    useEffect(() => {
        // Auto-run search whenever a pill-style filter changes. Typed inputs
        // (q / cpv / min / max / municipio / provincia) are submitted via the form button.
        // Re-use the AI mode if user already opted in and has a query.
        runSearch(1, useAiSearch && aiMode && q.trim().length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, procedureType, contractType, ambito, comunidad, openOnly, sortBy]);

    // Refresh per-bucket counters (à la InfoConcurso) when filters that
    // affect the totals change. We don't pass `contract_type` / `ambito` to
    // the breakdown query — they ARE the buckets being counted.
    useEffect(() => {
        const params = { open_only: openOnly };
        if (q && q.trim()) params.q = q.trim();
        if (cpv) params.cpv = cpv;
        if (minBudget) params.min_budget = minBudget;
        if (maxBudget) params.max_budget = maxBudget;
        if (state) params.state = state;
        if (procedureType) params.procedure_type = procedureType;
        if (municipio) params.municipio = municipio;
        if (provincia) params.provincia = provincia;
        if (comunidad) params.comunidad = comunidad;
        let cancelled = false;
        getPlcspBreakdown(params)
            .then((d) => {
                if (!cancelled) setBreakdown(d);
            })
            .catch(() => {
                if (!cancelled) setBreakdown(null);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, procedureType, comunidad, openOnly, results.length]);


    // While the backend is warming the PLCSP cache for the first time, poll
    // until results are available instead of leaving the user with a spinner.
    useEffect(() => {
        if (!cacheWarming) return;
        const id = setInterval(() => {
            runSearch(page, aiMode);
        }, 5000);
        return () => clearInterval(id);
    }, [cacheWarming, runSearch, page, aiMode]);

    const onSubmit = (e) => {
        e.preventDefault();
        // Use AI when toggle is on AND there's a query; literal otherwise.
        runSearch(1, useAiSearch && q.trim().length > 0);
    };

    const onImport = async (rec, withPdfs = false) => {
        setImportingId(rec.external_id);
        setImportingWithPdfs(withPdfs ? rec.external_id : null);
        const link = (rec.link || "").toLowerCase();
        const scrapeable = link.includes("contrataciondelestado.es");
        const externalLink = rec.link;
        try {
            const tender = await importFromPlcsp(rec.external_id, withPdfs);
            setImported((prev) => new Set([...prev, rec.external_id]));
            const pdfCount = (tender.documents || []).length;
            const pendingExternal = tender.pliegos_pending_external === true;
            // For autonomic platforms with public docs in another tab and no
            // scrapeable HTML, we still open the external link automatically
            // — useful when the headless harvester returns 0 docs.
            if (withPdfs && pdfCount === 0 && !pendingExternal && !scrapeable && externalLink) {
                window.open(externalLink, "_blank", "noopener");
            }
            // Show the post-import dialog so the user picks the next step.
            setPostImport({
                tender,
                hasPdfs: pdfCount > 0,
                pendingExternal,
                importNote: tender.import_note || "",
            });
        } catch (e) {
            const msg = e?.response?.data?.detail || "No se pudo importar";
            if (e?.response?.status === 409) {
                setImported((prev) => new Set([...prev, rec.external_id]));
                toast.warning(msg);
            } else {
                toast.error(msg);
            }
        } finally {
            setImportingId(null);
            setImportingWithPdfs(null);
        }
    };

    const totalPages = useMemo(() => Math.ceil(total / perPage) || 1, [total]);
    const fetchedLabel = useMemo(() => {
        if (!fetchedAt) return null;
        const d = new Date(fetchedAt * 1000);
        return d.toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
        });
    }, [fetchedAt]);

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="plcsp-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 mb-2">
                            — En vivo · feed oficial PLCSP
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Buscador PLCSP
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Licitaciones en tiempo real desde la Plataforma de Contratación
                            del Sector Público — incluye licitaciones ordinarias estatales,
                            plataformas autonómicas y locales agregadas y contratos menores.
                            Importa con un clic y deja que la IA prepare tu oferta.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => runSearch(page, aiMode)}
                        disabled={loading}
                        className="rounded-full border-ink-200 h-10 px-5 font-bold gap-2"
                        data-testid="plcsp-refresh-btn"
                    >
                        <RefreshCw
                            size={14}
                            className={loading ? "animate-spin" : ""}
                        />
                        {fetchedLabel ? `Actualizado ${fetchedLabel}` : "Refrescar"}
                    </Button>
                </div>

                {/* Save-as-alert prompt (visible after a search yielded results) */}
                <div className="mb-4">
                    <PlcspWatchlistButton
                        q={q}
                        cpv={cpv}
                        minBudget={minBudget}
                        maxBudget={maxBudget}
                        state={state}
                    />
                </div>

                {/* Search form */}
                <form
                    onSubmit={onSubmit}
                    className="bg-white border border-ink-100 rounded-2xl p-5 md:p-6 shadow-pop mb-6"
                    data-testid="plcsp-filters"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <Filter size={16} className="text-brand-600" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500">
                            Filtros
                        </span>
                    </div>
                    <div className="grid lg:grid-cols-12 gap-4">
                        <div className="lg:col-span-5">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                Palabra clave
                            </Label>
                            <div className="relative">
                                <Search
                                    size={15}
                                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400"
                                />
                                <Input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder={
                                        useAiSearch
                                            ? 'Pregunta en lenguaje natural: "obras de carretera en Galicia entre 500k y 2M"'
                                            : "Ej. mantenimiento, software, obra, energía…"
                                    }
                                    className={`pl-10 rounded-xl h-11 font-medium ${
                                        useAiSearch
                                            ? "border-violet-200 focus-visible:ring-violet-400 placeholder:text-violet-400/70 placeholder:italic"
                                            : "border-ink-100"
                                    }`}
                                    data-testid="plcsp-q"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                CPV (prefijo · opcional)
                            </Label>
                            <Input
                                value={cpv}
                                onChange={(e) => setCpv(e.target.value)}
                                placeholder="Ej. 48000000"
                                className="rounded-xl border-ink-100 h-11 font-mono placeholder:text-ink-300 placeholder:italic"
                                data-testid="plcsp-cpv"
                            />
                        </div>
                        <div className="lg:col-span-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                Mín. € (opcional)
                            </Label>
                            <Input
                                type="number"
                                value={minBudget}
                                onChange={(e) => setMinBudget(e.target.value)}
                                placeholder="Sin mínimo"
                                className="rounded-xl border-ink-100 h-11 font-mono placeholder:text-ink-300 placeholder:italic"
                                data-testid="plcsp-min"
                            />
                        </div>
                        <div className="lg:col-span-2">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                Máx. € (opcional)
                            </Label>
                            <Input
                                type="number"
                                value={maxBudget}
                                onChange={(e) => setMaxBudget(e.target.value)}
                                placeholder="Sin máximo"
                                className="rounded-xl border-ink-100 h-11 font-mono placeholder:text-ink-300 placeholder:italic"
                                data-testid="plcsp-max"
                            />
                        </div>
                        <div className="lg:col-span-1 flex items-end gap-2">
                            <Button
                                type="submit"
                                disabled={loading}
                                className={`flex-1 rounded-xl h-11 font-bold shadow-pop hover:scale-[1.02] transition-all ${
                                    useAiSearch && q.trim()
                                        ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                                        : "bg-brand-600 hover:bg-brand-700"
                                }`}
                                data-testid="plcsp-search-btn"
                                title={
                                    useAiSearch && q.trim()
                                        ? "Búsqueda IA (Claude expande sinónimos y CPV)"
                                        : "Búsqueda literal"
                                }
                            >
                                {loading ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : useAiSearch && q.trim() ? (
                                    <Sparkles size={16} />
                                ) : (
                                    <Search size={16} />
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                        <label
                            className="inline-flex items-center gap-2 cursor-pointer select-none rounded-full bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 px-3 h-9 hover:from-violet-100 hover:to-fuchsia-100 transition-all"
                            data-testid="plcsp-ai-toggle"
                        >
                            <input
                                type="checkbox"
                                checked={useAiSearch}
                                onChange={(e) => setUseAiSearch(e.target.checked)}
                                className="w-4 h-4 accent-violet-600 cursor-pointer"
                            />
                            <Sparkles size={13} className="text-violet-600" />
                            <span className="text-xs font-bold text-violet-700">
                                Búsqueda IA
                            </span>
                            <span className="text-[10px] font-mono text-violet-500 opacity-80">
                                Claude 4.5
                            </span>
                        </label>
                        <div className="text-xs text-ink-400 font-medium italic">
                            {useAiSearch
                                ? 'La IA expande "saas" → software, cloud, licencias, CPV 72/48…'
                                : "Búsqueda literal (sin sinónimos)"}
                        </div>
                    </div>
                    {useAiSearch && !q.trim() && (
                        <div
                            className="mt-3 flex items-start gap-2 flex-wrap"
                            data-testid="plcsp-ai-examples"
                        >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-violet-400 mt-1.5">
                                Prueba:
                            </span>
                            {[
                                "obras de carretera en Galicia entre 500k y 2M",
                                "mantenimiento de software en Madrid",
                                "suministro de equipamiento médico abierto",
                                "limpieza de edificios públicos en Andalucía",
                            ].map((ex) => (
                                <button
                                    key={ex}
                                    type="button"
                                    onClick={() => {
                                        setQ(ex);
                                        // Defer to the next tick so React commits
                                        // the new `q` before we call runSearch.
                                        setTimeout(() => runSearch(1, true), 0);
                                    }}
                                    className="text-[11px] font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 hover:border-violet-300 rounded-full px-3 h-7 transition-all"
                                    data-testid={`plcsp-ai-example-${ex.slice(0, 12)}`}
                                >
                                    {ex}
                                </button>
                            ))}
                        </div>
                    )}
                    {(q || cpv || minBudget || maxBudget || state || procedureType || contractType || ambito || municipio || provincia || comunidad) && (
                        <div className="mt-3 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={async () => {
                                    const name = window.prompt(
                                        "Nombre para esta búsqueda guardada (la verás en 'Mis búsquedas'):",
                                        q ? q.slice(0, 60) : "Mi búsqueda",
                                    );
                                    if (!name || !name.trim()) return;
                                    try {
                                        await createSavedSearch({
                                            name: name.trim(),
                                            q: q.trim(),
                                            cpv: cpv.trim(),
                                            min_budget: minBudget ? Number(minBudget) : null,
                                            max_budget: maxBudget ? Number(maxBudget) : null,
                                            state,
                                            open_only: openOnly,
                                            procedure_type: procedureType,
                                            contract_type: contractType,
                                            ambito,
                                            municipio: municipio.trim(),
                                            provincia: provincia.trim(),
                                            comunidad,
                                            use_ai: !!(useAiSearch && q.trim()),
                                        });
                                        toast.success(
                                            `"${name.trim()}" guardada. Ve a "Mis búsquedas" en el menú lateral.`,
                                        );
                                    } catch (e) {
                                        toast.error(
                                            e?.response?.data?.detail ||
                                                "No se pudo guardar la búsqueda",
                                        );
                                    }
                                }}
                                className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 h-7 transition-all"
                                data-testid="plcsp-save-search-btn"
                            >
                                <Star size={12} className="fill-amber-400 text-amber-500" />
                                Guardar esta búsqueda
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setQ("");
                                    setCpv("");
                                    setMinBudget("");
                                    setMaxBudget("");
                                    setState("");
                                    setProcedureType("");
                                    setContractType("");
                                    setAmbito("");
                                    setMunicipio("");
                                    setProvincia("");
                                    setComunidad("");
                                }}
                                className="text-xs font-bold text-ink-500 hover:text-brand-600 transition-colors"
                                data-testid="plcsp-clear-filters"
                            >
                                ← Limpiar filtros
                            </button>
                        </div>
                    )}
                    {/* Location filters: municipio, provincia, comunidad autónoma */}
                    <div className="mt-4 pt-4 border-t border-ink-100">
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin size={13} className="text-emerald-600" />
                            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500">
                                Ubicación
                            </span>
                        </div>
                        <div className="grid lg:grid-cols-12 gap-3">
                            <div className="lg:col-span-4">
                                <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                    Municipio
                                </Label>
                                <Input
                                    value={municipio}
                                    onChange={(e) => setMunicipio(e.target.value)}
                                    placeholder="Ej. Sevilla, Bilbao, Valencia…"
                                    className="rounded-xl border-ink-100 h-10 font-medium"
                                    data-testid="plcsp-municipio"
                                />
                            </div>
                            <div className="lg:col-span-4">
                                <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                    Provincia
                                </Label>
                                <Input
                                    value={provincia}
                                    onChange={(e) => setProvincia(e.target.value)}
                                    placeholder="Ej. Barcelona, Cádiz, Toledo…"
                                    className="rounded-xl border-ink-100 h-10 font-medium"
                                    data-testid="plcsp-provincia"
                                />
                            </div>
                            <div className="lg:col-span-4">
                                <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                    Comunidad autónoma
                                </Label>
                                <select
                                    value={comunidad}
                                    onChange={(e) => setComunidad(e.target.value)}
                                    className="w-full rounded-xl border border-ink-100 h-10 px-3 font-medium text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400 transition-all"
                                    data-testid="plcsp-comunidad"
                                >
                                    <option value="">Todas las comunidades</option>
                                    {communities.map((c) => (
                                        <option key={c.key} value={c.key}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 mt-4 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-ink-400 mr-2">
                            Tipo de contrato:
                        </span>
                        {[
                            ["", "Todos", breakdown?.total ?? null],
                            ["obras", "Obras", breakdown?.contract_type?.obras ?? null],
                            ["suministros", "Suministros", breakdown?.contract_type?.suministros ?? null],
                            ["servicios", "Servicios", breakdown?.contract_type?.servicios ?? null],
                            ["otros", "Otros", breakdown?.contract_type?.otros ?? null],
                        ].map(([s, label, count]) => (
                            <button
                                type="button"
                                key={`ct-${s || "all"}`}
                                onClick={() => {
                                    setContractType(s);
                                }}
                                className={`px-3 h-8 text-xs font-bold rounded-full transition-all inline-flex items-center gap-1.5 ${
                                    contractType === s
                                        ? "bg-rose-600 text-white shadow-pop"
                                        : "bg-ink-50 text-ink-500 hover:bg-rose-50 hover:text-rose-700"
                                }`}
                                data-testid={`plcsp-ctype-${s || "all"}`}
                            >
                                {label}
                                {count != null && (
                                    <span
                                        className={`text-[10px] font-mono font-semibold ${
                                            contractType === s
                                                ? "text-white/80"
                                                : "text-ink-400"
                                        }`}
                                    >
                                        {count.toLocaleString("es-ES")}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-ink-400 mr-2">
                            Tramitación:
                        </span>
                        {[
                            ["", "Todos"],
                            ["ordinaria", "Ordinarias"],
                            ["menor", "Contratos menores"],
                        ].map(([s, label]) => (
                            <button
                                type="button"
                                key={`pt-${s || "all"}`}
                                onClick={() => {
                                    setProcedureType(s);
                                }}
                                className={`px-3 h-8 text-xs font-bold rounded-full transition-all ${
                                    procedureType === s
                                        ? "bg-amber-600 text-white shadow-pop"
                                        : "bg-ink-50 text-ink-500 hover:bg-amber-50 hover:text-amber-700"
                                }`}
                                data-testid={`plcsp-proctype-${s || "all"}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-ink-400 mr-2">
                            Ámbito:
                        </span>
                        {[
                            ["", "Todos", breakdown?.total ?? null],
                            ["estatal", "Estatal · PLACE", breakdown?.ambito?.estatal ?? null],
                            ["agregada", "Autonómico / Local", breakdown?.ambito?.agregada ?? null],
                        ].map(([s, label, count]) => (
                            <button
                                type="button"
                                key={`amb-${s || "all"}`}
                                onClick={() => {
                                    setAmbito(s);
                                }}
                                className={`px-3 h-8 text-xs font-bold rounded-full transition-all inline-flex items-center gap-1.5 ${
                                    ambito === s
                                        ? "bg-violet-600 text-white shadow-pop"
                                        : "bg-ink-50 text-ink-500 hover:bg-violet-50 hover:text-violet-700"
                                }`}
                                data-testid={`plcsp-ambito-${s || "all"}`}
                            >
                                {label}
                                {count != null && (
                                    <span
                                        className={`text-[10px] font-mono font-semibold ${
                                            ambito === s
                                                ? "text-white/80"
                                                : "text-ink-400"
                                        }`}
                                    >
                                        {count.toLocaleString("es-ES")}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-ink-400 mr-2">
                            Estado:
                        </span>
                        {[
                            ["", "Todos"],
                            ["PUB", "Publicadas"],
                            ["EVL", "En evaluación"],
                            ["RES", "Resueltas"],
                            ["ADJ", "Adjudicadas"],
                        ].map(([s, label]) => (
                            <button
                                type="button"
                                key={s || "all"}
                                onClick={() => {
                                    setState(s);
                                }}
                                className={`px-3 h-8 text-xs font-bold rounded-full transition-all ${
                                    state === s
                                        ? "bg-brand-600 text-white shadow-pop"
                                        : "bg-ink-50 text-ink-500 hover:bg-brand-50 hover:text-brand-600"
                                }`}
                                data-testid={`plcsp-state-${s || "all"}`}
                            >
                                {label}
                            </button>
                        ))}
                        <div className="ml-auto flex items-center gap-3">
                            <div
                                className="flex items-center gap-2"
                                data-testid="plcsp-sort-wrapper"
                            >
                                <span className="text-xs font-bold uppercase tracking-wider text-ink-400">
                                    Orden:
                                </span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-8 text-xs font-semibold bg-ink-50 hover:bg-ink-100 text-ink-800 rounded-full px-3 cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-brand-400"
                                    data-testid="plcsp-sort-select"
                                >
                                    <option value="recent">Recientes (mezclado)</option>
                                    <option value="deadline">Plazo más próximo</option>
                                    <option value="budget_desc">Importe ↓</option>
                                    <option value="budget_asc">Importe ↑</option>
                                </select>
                            </div>
                            <label
                                className="inline-flex items-center gap-2 cursor-pointer select-none"
                                data-testid="plcsp-open-only-toggle"
                            >
                                <input
                                    type="checkbox"
                                    checked={openOnly}
                                    onChange={(e) => {
                                        setOpenOnly(e.target.checked);
                                    }}
                                    className="w-4 h-4 accent-brand-600 cursor-pointer"
                                />
                                <span className="text-xs font-bold text-ink-700">
                                    Solo plazos abiertos
                                </span>
                            </label>
                        </div>
                    </div>
                </form>

                {/* AI interpretation banner */}
                {aiMode && !loading && (
                    <PlcspAiBanner
                        interpretation={aiInterpretation}
                        keywords={aiKeywords}
                        cpvCodes={aiCpvCodes}
                    />
                )}

                {/* Results header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-ink-500 font-medium">
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin text-brand-600" />
                                Buscando…
                            </span>
                        ) : cacheWarming ? (
                            <span className="inline-flex items-center gap-2 text-amber-700">
                                <Loader2 size={14} className="animate-spin" />
                                Descargando histórico PLCSP por primera vez (12 meses, ~1 millón de licitaciones)… puede tardar 12-18 minutos. Se reintentará automáticamente.
                            </span>
                        ) : (
                            <>
                                <span className="font-display font-bold text-ink-950 text-base">
                                    {total.toLocaleString("es-ES")}
                                </span>{" "}
                                resultado{total === 1 ? "" : "s"}
                                {openOnly && (
                                    <span className="text-xs font-bold text-emerald-700 bg-emerald-50 ml-2 px-2 py-1 rounded-full">
                                        plazo abierto
                                    </span>
                                )}
                                {cacheSize > 0 && (
                                    <span className="text-xs font-mono text-ink-400 ml-2">
                                        sobre {cacheSize.toLocaleString("es-ES")} en cache
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => runSearch(page - 1, aiMode)}
                                disabled={page <= 1 || loading}
                                className="rounded-full h-9 px-4 border-ink-200"
                                data-testid="plcsp-prev"
                            >
                                ← Anterior
                            </Button>
                            <span className="text-sm font-bold text-ink-700 px-3">
                                {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => runSearch(page + 1, aiMode)}
                                disabled={page >= totalPages || loading}
                                className="rounded-full h-9 px-4 border-ink-200"
                                data-testid="plcsp-next"
                            >
                                Siguiente →
                            </Button>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 p-4 rounded-2xl text-sm mb-4">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <div>{error}</div>
                    </div>
                )}

                {/* Results grid */}
                {!loading && results.length === 0 && !error && (
                    <PlcspEmptyState
                        openOnly={openOnly}
                        totalIncludingClosed={totalIncludingClosed}
                        onShowClosed={() => setOpenOnly(false)}
                    />
                )}

                <div className="space-y-3">
                    {results.map((r) => (
                        <PlcspResultCard
                            key={r.external_id}
                            r={r}
                            isImported={imported.has(r.external_id)}
                            isImporting={importingId === r.external_id}
                            importingWithPdfs={importingWithPdfs}
                            onImport={onImport}
                        />
                    ))}
                </div>
            </div>
            {/* Post-import dialog: ask whether to keep searching or open the
                newly created tender to see summary, escandallo, anexos, etc. */}
            <Dialog
                open={!!postImport}
                onOpenChange={(open) => !open && setPostImport(null)}
            >
                <DialogContent
                    className="rounded-2xl max-w-md"
                    data-testid="post-import-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-black flex items-center gap-2">
                            <CheckCircle2 size={20} className="text-emerald-600" />
                            Licitación importada
                        </DialogTitle>
                        <DialogDescription className="text-sm text-ink-600 pt-2 leading-relaxed">
                            <span className="block font-bold text-ink-950 mb-1">
                                {postImport?.tender?.title}
                            </span>
                            {postImport?.pendingExternal ? (
                                <>
                                    Estamos descargando los pliegos en segundo plano. El
                                    análisis IA arrancará automáticamente en cuanto los
                                    encontremos (≈30-60 s).
                                </>
                            ) : postImport?.hasPdfs ? (
                                <>
                                    Pliegos descargados — la IA ya está generando el
                                    resumen, escandallo y anexos.
                                </>
                            ) : (
                                <>
                                    {postImport?.importNote ||
                                        "Importada en tu panel. Sube los pliegos manualmente para arrancar el análisis IA."}
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-3 flex-col-reverse sm:flex-row pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setPostImport(null)}
                            className="rounded-full border-ink-200"
                            data-testid="post-import-keep-searching"
                        >
                            Seguir buscando
                        </Button>
                        <Button
                            onClick={() => {
                                const id = postImport?.tender?.id;
                                setPostImport(null);
                                if (id) navigate(`/app/licitacion/${id}`);
                            }}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white"
                            data-testid="post-import-view"
                        >
                            Ver licitación importada
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}
